import React, { useEffect, useState } from "react";

import { Alert, Box, Button, Stack } from "@mui/material";

import { ClipLoader } from "react-spinners";

import {
    getOpenAiAdsMappings,
    postOpenAiAdsMappings,
} from "helpers/destinations/openaiAds";

import SwalAlertComp from "components/SwalAlert";

import {
    areOpenAiMappingsValid,
    buildOpenAiMappingsPayload,
    mapStoredOpenAiMappings,
} from "./OpenAiAds.helpers";
import OpenAiAdsMappingContent from "./OpenAiAdsMappingContent";

const OpenAiAdsMappingEdit = ({ destinationId, onSaveSuccess }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [error, setError] = useState(null);

    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedMappings, setSelectedMappings] = useState([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

    useEffect(() => {
        fetchMappings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationId]);

    const fetchMappings = async () => {
        setLoading(true);
        setLoadError(null);
        setError(null);

        try {
            const response = await getOpenAiAdsMappings(destinationId);

            if (response.status && response.data) {
                const data = response.data;

                setConversions(data.conversions?.all_conversions || []);
                setEvents(data.conversions?.all_events || []);
                setSelectedMappings(mapStoredOpenAiMappings(data.mappings));
            } else {
                setLoadError(response.message || "Failed to fetch mappings");
            }
        } catch (err) {
            console.error("Error fetching OpenAI Ads mappings:", err);
            setLoadError("An error occurred while fetching mappings");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMappings = async () => {
        setSaving(true);
        setError(null);

        try {
            if (
                !selectedMappings.length ||
                !areOpenAiMappingsValid(selectedMappings, conversions)
            ) {
                setError(
                    "Select an event and OpenAI Ads conversion for every row, or remove the incomplete row.",
                );
                setSaving(false);
                return;
            }

            const response = await postOpenAiAdsMappings(
                destinationId,
                buildOpenAiMappingsPayload(selectedMappings, conversions),
            );

            if (response.status) {
                SwalAlertComp({
                    title: "Success",
                    message: "Mappings saved successfully!",
                    icon: "success",
                    timer: 3000,
                });

                if (onSaveSuccess) {
                    setTimeout(() => {
                        onSaveSuccess();
                    }, 1000);
                }
            } else {
                setError(response.message || "Failed to save mappings");
            }
        } catch (err) {
            console.error("Error saving OpenAI Ads mappings:", err);
            setError("An error occurred while saving mappings");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setIsSubmitDisabled(
            !selectedMappings.length ||
                !areOpenAiMappingsValid(selectedMappings, conversions),
        );
    }, [selectedMappings, conversions]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
            >
                <ClipLoader size={24} color="#666" />
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <OpenAiAdsMappingContent
                conversions={conversions}
                events={events}
                selectedMappings={selectedMappings}
                setSelectedMappings={setSelectedMappings}
                loadError={loadError}
                onRetry={fetchMappings}
                isScrollable={false}
                maxHeight={null}
            />

            {!loadError && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveMappings}
                        disabled={saving || isSubmitDisabled}
                    >
                        {saving ? (
                            <>
                                <ClipLoader size={16} color="#fff" />
                                <span style={{ marginLeft: "8px" }}>
                                    Saving...
                                </span>
                            </>
                        ) : (
                            "Save Mappings"
                        )}
                    </Button>
                </Box>
            )}
        </Stack>
    );
};

export default OpenAiAdsMappingEdit;
